import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { accessToken } from '../../../../configuration/tokenHandling/accessToken';
import type { TermedLevel } from '../../common/termedLevel/types';
import { getMarketplaceBackend } from '../../config/pathURL/config';
import { decodeJson, transformRtkQueryError } from '../api';
import { FetchError } from '../FetchError';
import { ApiContractsCodec } from './contract.types';
import { mapApiContracts } from './contractMapper';

export interface FetchContractsOptions {
    paymentMethodRequired?: boolean;
}

export interface UpdateContractData {
    productId: string;
    resourceIds: string[];
    renewsToLevel?: string;
}

export interface ContractApiError {
    message: string;
    name?: string;
    statusCode?: number;
    problemJsonText?: string;
}

const toContractApiError = (error: unknown): ContractApiError => {
    const contractError = error as Partial<ContractApiError>;
    return {
        message: contractError.message ?? String(error),
        name: contractError.name,
        statusCode: contractError.statusCode,
        problemJsonText: contractError.problemJsonText,
    };
};

const toFetchError = (response: { status: number | string; data?: unknown }, meta?: { response?: Response }) => {
    const responseText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
    return new FetchError(
        meta?.response ??
            ({
                ok: false,
                status: Number(response.status),
                statusText: '',
                url: '',
                headers: { get: () => null },
            } as unknown as Response),
        responseText ?? ''
    );
};

export const contractApiSlice = createApi({
    reducerPath: 'contractApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getMarketplaceBackend(),
        prepareHeaders: headers => {
            const token = accessToken.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Contracts'],
    endpoints: builder => ({
        getContracts: builder.query<TermedLevel[], FetchContractsOptions | undefined>({
            query: options => ({
                url: '/contracts',
                params: options?.paymentMethodRequired ? { payment_method_required: true } : undefined,
            }),
            transformResponse: (response: unknown) => mapApiContracts()(decodeJson(ApiContractsCodec)(response)),
            transformErrorResponse: (response, meta) => {
                try {
                    transformRtkQueryError(response, [401]);
                } catch (error) {
                    return toContractApiError(error);
                }
                return toContractApiError(toFetchError(response, meta));
            },
            providesTags: ['Contracts'],
        }),
        updateContract: builder.mutation<null, UpdateContractData>({
            query: ({ productId, resourceIds, renewsToLevel }) => ({
                url: `/contracts/${productId}/config`,
                method: 'POST',
                body: {
                    resource_ids: resourceIds,
                    renews_to_level: renewsToLevel,
                },
            }),
            transformResponse: () => null,
            transformErrorResponse: (response, meta) => {
                try {
                    transformRtkQueryError(response);
                } catch (error) {
                    return toContractApiError(error);
                }
                return toContractApiError(toFetchError(response, meta));
            },
            invalidatesTags: ['Contracts'],
        }),
    }),
});

export const { useGetContractsQuery } = contractApiSlice;
